import { motion } from 'framer-motion';
import styled from 'styled-components';
import { shimmerAnimation } from 'styles/animations';

export const ResourceCardWrapper = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 0px 5px;

  border-radius: 20px;
  box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.1);
  background: #ffffff;

  cursor: pointer;
`;

export const ResourceCardContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding: 16px;

  & > :not(:last-child) {
    margin-bottom: 16px;
  }

  border-radius: 20px 20px 0px 0px;

  position: relative;
`;

export const ResourceCardNewTabIndicator = styled.div`
  width: 28px;
  height: 28px;

  position: absolute;
  top: 16px;
  right: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.16);
  pointer-events: none;
  z-index: 3;
`;

export const ResourceCardPaywallContent = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 3px 6px;
  border-radius: 4px;
  background: var(--color-data-company-background-secondary);
`;

export const ResourceCardIconContainer = styled.div`
  width: 40px;
  height: 40px;

  position: absolute;
  top: 8px;
  right: 8px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 100%;
  background: var(--color-primary);
  z-index: 2;
`;

export const ResourceCardIcon = styled.img`
  width: 22px;
  height: 22px;

  object-fit: cover;
`;

export const ResourceCardImageContainer = styled(motion.div)`
  position: relative;
  overflow: hidden;
`;

export const ResourceCardImageTagContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
`;

export const ResourceCardLikesContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

export const ResourceCardCommentsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

export const ResourceCardImageLikesContainer = styled.div`
  width: fit-content;

  position: absolute;
  top: 0px;
  left: 0px;

  display: flex;
  flex-direction: row;
  align-items: center;

  & > :not(:last-child) {
    margin-right: 2px;
  }

  padding: 4px 8px;

  border-radius: 0px 0px 8px 0px;
  background: #fff;
  box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.1);
`;

export const ResourceCardImageLikesText = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: normal;

  color: var(--color-text);
`;

export const ResourceCardImage = styled(motion.img).attrs({
  className: 'loading',
  onLoad: (e: any) => {
    e.target.classList.remove('loading');
  },
})`
  width: 100%;
  height: 144px;
  object-fit: cover;

  &.loading {
    ${shimmerAnimation()}

    background: linear-gradient(
      to right,
      rgba(242, 242, 242, 0.1) 4%,
      rgba(242, 242, 242, 0.5) 25%,
      rgba(242, 242, 242, 0.1) 36%
    );
    background-size: 1000px 100%;
  }
`;

export const ResourceCardClients = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
`;

interface ResourceCardTitleProps {
  bestOf2024?: boolean;
}

export const ResourceCardTitle = styled.span.attrs({
  'data-id': 'resource-card-title',
})<ResourceCardTitleProps>`
  max-width: ${({ bestOf2024 }) => (!bestOf2024 ? '100%' : 'calc(100% - 40px)')};
  font-weight: 400;
  font-size: 18px;
  line-height: normal;
  color: var(--color-text);

  white-space: break-spaces;

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`;

export const ResourceCardDescription = styled(motion.p).attrs({
  transition: {
    duration: 0.3,
    ease: 'easeInOut',
  },
})`
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  color: rgba(23, 28, 51, 0.5);

  white-space: break-spaces;

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`;

export const ResourceCardTagsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
`;

export const ResourceCardTag = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  color: var(--color-text);

  padding: 2px 8px;

  border-radius: 28px;
  background: rgba(23, 28, 51, 0.1);

  cursor: pointer;
`;

export const ResourceCardLinkAndPaywallContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export const ResourceCardLinkContainer = styled.div`
  display: flex;
  align-items: center;

  & > :not(:last-child) {
    margin-right: 8px;
  }
`;

export const ResourceCardLinkFavicon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: cover;
`;

export const ResourceCardLink = styled.a`
  font-weight: 400;
  font-size: 12px;
  line-height: normal;
  color: rgba(23, 28, 51, 0.5);
  text-decoration-line: underline;
`;

export const ResourceCardExtraInformationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  padding: 8px 16px;

  & > :not(:last-child) {
    margin-bottom: 4px;
  }

  border-radius: 0px 0px 20px 20px;
  border-top: 1px solid rgba(23, 28, 51, 0.1);
`;

export const ResourceCardExtraInformationRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  & > :not(:last-child) {
    margin-right: 4px;
  }
`;

// RESOURCE CARD PEOPLE
export const ResourceCardPeopleWrapper = styled.div`
  min-width: 50%;
`;

export const ResourceCardPeopleOverallContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 9px;
`;

interface ResourceCardPeopleContainerProps {
  hasMore: boolean;
}

export const ResourceCardPeopleContainer = styled(
  ResourceCardPeopleWrapper
)<ResourceCardPeopleContainerProps>`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;

  & > :not(:first-child) {
    margin-right: -8px;
  }

  ${({ hasMore }) =>
    hasMore &&
    `
    & > :first-child {
      margin-left: 3px;
    }
  `}
`;

export const ResourceCardPeopleCounterContainer = styled.div`
  width: 22px;
  height: 22px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 100%;
  border: 2px solid var(--color-data-people-foreground);
  background: var(--color-data-people-background-secondary);
`;

export const ResourceCardPeopleCounter = styled.span`
  font-weight: 700;
  font-size: 11px;
  line-height: normal;
  text-align: center;
  color: var(--color-data-people-foreground);
`;

export const ResourceCardPeopleSinglePersonContainer = styled(ResourceCardPeopleWrapper)`
  display: flex;
  align-items: center;

  & > :not(:last-child) {
    margin-right: 4px;
  }
`;

export const ResourceCardPeopleSinglePersonName = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: normal;
  color: var(--color-text);
`;

// RESOURCE CARD COMPANIES
export const ResourceCardCompaniesKyuContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 4px 12px;
  border-radius: 32px;
  background: var(--color-primary);
`;

interface ResourceCardCompaniesKyuProps {
  highlight?: boolean;
}

export const ResourceCardCompaniesKyu = styled.span<ResourceCardCompaniesKyuProps>`
  font-weight: ${({ highlight }) => (highlight ? 700 : 500)};
  font-size: 14px;
  line-height: normal;
  color: var(--dark-theme-text-primary, #fff);
`;

export const ResourceCardCompaniesContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;

  & > :not(:first-child) {
    margin-right: -5px;
  }
`;

export const ResourceCardNLPMatchesRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  min-height: 20px;
  font-size: 12px;
  color: rgba(23, 28, 51, 0.5);
`;

export const ResourceCardNLPMatchesLabel = styled.span`
  color: rgba(23, 28, 51, 0.5);
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 1.6px;
`;
export const ResourceCardNLPMatchesDetail = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
`;
export const ResourceCardNLPMatchesItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  span {
    color: rgba(23, 28, 51, 0.5);
  }
`;

export const ResourceCardPeopleTagWrapper = styled.div`
  width: fit-content;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;

  padding: 2px 10px 2px 4px;

  border-radius: 100px;
  background: rgba(23, 28, 51, 0.6);
`;

export const ResourceCardPeopleTagText = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: normal;

  color: #f7f7f7;
`;

//HEADER
export const ResourceCardHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ResourceCardHeaderLeftContainer = styled.div`
  width: fit-content;
  position: relative;
  z-index: 4;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const ResourceCardHeaderRightContainer = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ResourceCardHeaderTitle = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: normal;

  color: var(--color-text);
`;

export const ResourceCardHeaderDate = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: normal;
  letter-spacing: 1.4px;
  text-transform: uppercase;

  color: rgba(23, 28, 51, 0.5);
`;

// RESOURCE CARD COMPANIES
export const ResourceCardHeaderCompaniesContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;

  & > :not(:first-child) {
    margin-right: -5px;
  }
`;
